import React, { useState, useEffect } from 'react';
import { Tabs, Button, Dropdown, Modal, Table, DatePicker, App, Space, Spin, Card, Row, Col, Statistic, Select, Grid } from 'antd';
import { 
    AppstoreOutlined, 
    QrcodeOutlined, 
    FileTextOutlined, 
    DownloadOutlined,
    CalendarOutlined,
    InboxOutlined,
    SwapOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import ProductList from './ProductList';
import AssetTracker from './AssetTracker';
import LowStockAlert from '../components/Inventory/LowStockAlert';
import api from '../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;


const InventoryPage = () => {
    const { message } = App.useApp();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [activeTab, setActiveTab] = useState('1');
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [dateRangeModalVisible, setDateRangeModalVisible] = useState(false);
    const [monthPickerVisible, setMonthPickerVisible] = useState(false);
    const [quarterPickerVisible, setQuarterPickerVisible] = useState(false);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedQuarter, setSelectedQuarter] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [assets, setAssets] = useState([]);
    const [stats, setStats] = useState({
        totalItems: 0,
        totalAssets: 0,
        lowStockItems: 0,
        assignedAssets: 0,
        totalInverters: 0,
        totalPanels: 0
    });

    // Available years for selection (current year and last 5 years)
    const availableYears = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
    
    // Months for selection
    const months = [
        { value: 0, label: 'January' },
        { value: 1, label: 'February' },
        { value: 2, label: 'March' },
        { value: 3, label: 'April' },
        { value: 4, label: 'May' },
        { value: 5, label: 'June' },
        { value: 6, label: 'July' },
        { value: 7, label: 'August' },
        { value: 8, label: 'September' },
        { value: 9, label: 'October' },
        { value: 10, label: 'November' },
        { value: 11, label: 'December' }
    ];

    // Quarters for selection
    const quarters = [
        { value: 1, label: 'Q1 (Jan - Mar)' },
        { value: 2, label: 'Q2 (Apr - Jun)' },
        { value: 3, label: 'Q3 (Jul - Sep)' },
        { value: 4, label: 'Q4 (Oct - Dec)' }
    ];

    // Helper function to format date
    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0];
    };

    // Helper function to get start of selected month
    const getStartOfSelectedMonth = (year, month) => {
        return new Date(year, month, 1);
    };

    // Helper function to get end of selected month
    const getEndOfSelectedMonth = (year, month) => {
        return new Date(year, month + 1, 0);
    };

    // Helper function to get start of selected quarter
    const getStartOfSelectedQuarter = (year, quarter) => {
        const startMonth = (quarter - 1) * 3;
        return new Date(year, startMonth, 1);
    };

    // Helper function to get end of selected quarter
    const getEndOfSelectedQuarter = (year, quarter) => {
        const endMonth = quarter * 3; // Month after last month of quarter
        return new Date(year, endMonth, 0);
    };

    // Helper function to check if date is in range
    const isDateInRange = (date, startDate, endDate) => {
        if (!date) return false;
        const checkDate = new Date(date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return checkDate >= start && checkDate <= end;
    };

    // Helper function to calculate opening stock (assets that existed BEFORE start date and are still in stock)
    const calculateOpeningStock = (itemId, startDate) => {
        // Get all assets for this item that were created BEFORE the start date
        const assetsBeforeStart = assets.filter(asset => 
            asset.itemId === itemId && 
            new Date(asset.createdAt) < new Date(startDate)
        );
        
        // Of those assets, count how many are STILL in stock (not assigned, faulty, or retired)
        // We need to check their current status, not just status at the time
        const inStockBeforeStart = assetsBeforeStart.filter(asset => 
            asset.status === 'InStock'
        ).length;
        
        // Also, we need to consider assets that were assigned AFTER the start date
        // These should NOT be counted in opening stock because they were assigned during the period
        const assignedAfterStart = assetsBeforeStart.filter(asset =>
            asset.status === 'Assigned' &&
            asset.assignedDate &&
            new Date(asset.assignedDate) >= new Date(startDate)
        ).length;
        
        // Opening stock = Assets that were in stock before start and remained in stock
        // Or were assigned after start (they were in stock at the beginning of the period)
        return inStockBeforeStart + assignedAfterStart;
    };

    // Fetch data when component mounts
    useEffect(() => {
        fetchInventoryData();
        fetchStats();
        fetchItems();
        fetchAssets();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await api.get('/inventory/stats');
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch inventory stats');
            message.error('Failed to fetch inventory statistics.');
        }
    };

    const fetchItems = async () => {
        try {
            const response = await api.get('/inventory/items');
            if (response.success) {
                setItems(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch items:', error);
            message.error('Failed to fetch inventory items.');
        }
    };

    const fetchAssets = async () => {
        try {
            const response = await api.get('/inventory/assets');
            if (response.success) {
                setAssets(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch assets:', error);
            message.error('Failed to fetch inventory assets.');
        }
    };

    const fetchInventoryData = async () => {
        setLoading(true);
        try {
            // The individual fetch functions (fetchStats, fetchItems, fetchAssets)
            // are now called in useEffect. This function can be simplified
            // or removed if its only purpose was to orchestrate these fetches.
            // For now, we'll keep it as a wrapper if needed for other logic.
            await Promise.all([
                fetchStats(),
                fetchItems(),
                fetchAssets()
            ]);
            message.success('Inventory data loaded successfully');
        } catch (error) {
            console.error('Failed to fetch inventory data:', error);
            message.error('Failed to fetch inventory data.');
        } finally {
            setLoading(false);
        }
    };

    // The calculateStats function is no longer needed if stats are fetched directly from the API.
    // const calculateStats = (itemsData, assetsData) => {
    //     // Ensure itemsData is an array
    //     const itemsArray = Array.isArray(itemsData) ? itemsData : [];
    //     const assetsArray = Array.isArray(assetsData) ? assetsData : [];
        
    //     // Count inverters and panels based on itemType enum
    //     const inverters = itemsArray.filter(item => item.itemType === 'Inverter');
    //     const panels = itemsArray.filter(item => item.itemType === 'SolarPanel');
        
    //     // Get bulk inventory data (you might need to fetch this separately)
    //     const lowStockItems = itemsArray.filter(item => {
    //         // This assumes you have a way to get current stock levels
    //         // You might need to fetch bulkInventory data
    //         return false; // Placeholder
    //     }).length;

    //     const assignedAssets = assetsArray.filter(asset => asset.status === 'Assigned').length;

    //     setStats({
    //         totalItems: itemsArray.length,
    //         totalAssets: assetsArray.length,
    //         lowStockItems: lowStockItems,
    //         assignedAssets: assignedAssets,
    //         totalInverters: inverters.length,
    //         totalPanels: panels.length
    //     });
    // };

    const generateMonthlyReport = () => {
        if (selectedMonth === null) {
            message.warning('Please select a month');
            return;
        }
        
        const startDate = formatDate(getStartOfSelectedMonth(selectedYear, selectedMonth));
        const endDate = formatDate(getEndOfSelectedMonth(selectedYear, selectedMonth));
        
        generateReport('monthly', startDate, endDate);
        setMonthPickerVisible(false);
    };

    const generateQuarterlyReport = () => {
        if (!selectedQuarter) {
            message.warning('Please select a quarter');
            return;
        }
        
        const startDate = formatDate(getStartOfSelectedQuarter(selectedYear, selectedQuarter));
        const endDate = formatDate(getEndOfSelectedQuarter(selectedYear, selectedQuarter));
        
        generateReport('quarterly', startDate, endDate);
        setQuarterPickerVisible(false);
    };

    const generateReport = async (type, startDate, endDate) => {
        setLoading(true);
        try {
            console.log(`Generating ${type} report from ${startDate} to ${endDate}`);

            const params = { startDate, endDate, type };
            console.log('Fetching report with params:', params);
            const response = await api.get('/inventory/report', { params });
            
            if (response.success) {
                setReportData(response.data);
                setReportModalVisible(true);
                message.success('Report generated successfully');
            } else {
                message.error(response.message || 'Failed to generate report');
            }
        } catch (error) {
            console.error('Failed to generate report:', error);
            message.error('Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const generateReportFromData = (startDate, endDate) => {
        // Ensure items and assets are arrays
        const itemsArray = Array.isArray(items) ? items : [];
        const assetsArray = Array.isArray(assets) ? assets : [];
        
        // Filter assets based on date range for received count
        const assetsReceivedInPeriod = assetsArray.filter(asset => {
            const assetDate = asset.createdAt;
            return assetDate ? isDateInRange(assetDate, startDate, endDate) : false;
        });

        // Filter assets based on date range for assigned count
        const assetsAssignedInPeriod = assetsArray.filter(asset => {
            return asset.assignedDate ? isDateInRange(asset.assignedDate, startDate, endDate) : false;
        });

        // Group items by type
        const inverters = itemsArray
            .filter(item => item.itemType === 'Inverter')
            .map(item => {
                // Calculate opening stock (assets that existed BEFORE start date and are/were in stock)
                const openingStock = calculateOpeningStock(item.id, startDate);
                
                // Calculate received in period
                const received = assetsReceivedInPeriod.filter(a => 
                    a.itemId === item.id && a.status === 'InStock'
                ).length;
                
                // Calculate assigned in period
                const assigned = assetsAssignedInPeriod.filter(a => 
                    a.itemId === item.id
                ).length;
                
                // Calculate closing stock (assets currently in stock as of end date)
                const closingStock = assetsArray.filter(a => 
                    a.itemId === item.id && 
                    a.status === 'InStock' &&
                    (!a.assignedDate || new Date(a.assignedDate) > new Date(endDate))
                ).length;

                return {
                    id: item.id,
                    model: item.modelNo || item.itemName,
                    itemName: item.itemName,
                    openingStock: openingStock,
                    received: received,
                    assigned: assigned,
                    closingStock: closingStock,
                    warrantyMonths: item.warrantyMonths,
                    hsnCode: item.hsnCode
                };
            });

        const panels = itemsArray
            .filter(item => item.itemType === 'SolarPanel')
            .map(item => {
                // Calculate opening stock (assets that existed BEFORE start date and are/were in stock)
                const openingStock = calculateOpeningStock(item.id, startDate);
                
                // Calculate received in period
                const received = assetsReceivedInPeriod.filter(a => 
                    a.itemId === item.id && a.status === 'InStock'
                ).length;
                
                // Calculate assigned in period
                const assigned = assetsAssignedInPeriod.filter(a => 
                    a.itemId === item.id
                ).length;
                
                // Calculate closing stock (assets currently in stock as of end date)
                const closingStock = assetsArray.filter(a => 
                    a.itemId === item.id && 
                    a.status === 'InStock' &&
                    (!a.assignedDate || new Date(a.assignedDate) > new Date(endDate))
                ).length;

                return {
                    id: item.id,
                    model: item.modelNo || item.itemName,
                    itemName: item.itemName,
                    wattage: item.capacity || 0,
                    openingStock: openingStock,
                    received: received,
                    assigned: assigned,
                    closingStock: closingStock,
                    warrantyMonths: item.warrantyMonths,
                    hsnCode: item.hsnCode
                };
            });

        // Asset movements (assigned assets in the period)
        const assetMovements = assetsArray
            .filter(asset => asset.status === 'Assigned' && asset.assignedDate && isDateInRange(asset.assignedDate, startDate, endDate))
            .map(asset => {
                const item = itemsArray.find(i => i.id === asset.itemId);
                return {
                    id: asset.id,
                    serialNumber: asset.serialNumber,
                    model: item?.modelNo || item?.itemName || 'N/A',
                    itemType: item?.itemType,
                    assignedTo: asset.customerId,
                    assignedDate: asset.assignedDate,
                    warrantyExpiry: asset.warrantyExpiry,
                    capacity: asset.capacity
                };
            });

        // Calculate summary
        const summary = {
            startDate,
            endDate,
            totalInverters: inverters.reduce((sum, item) => sum + item.closingStock, 0),
            totalInvertersModels: inverters.length,
            totalPanels: panels.reduce((sum, item) => sum + item.closingStock, 0),
            totalPanelsModels: panels.length,
            totalAssets: assetsArray.length,
            assignedAssets: assetsArray.filter(a => a.status === 'Assigned').length,
            availableAssets: assetsArray.filter(a => a.status === 'InStock').length,
            faultyAssets: assetsArray.filter(a => a.status === 'Faulty').length,
            retiredAssets: assetsArray.filter(a => a.status === 'Retired').length
        };

        return {
            inverters,
            panels,
            assetMovements,
            summary,
            generatedAt: new Date().toLocaleString()
        };
    };

    const exportToExcel = () => {
        if (!reportData) return;

        try {
            const wb = XLSX.utils.book_new();

            // Inverters worksheet
            if (reportData.inverters && reportData.inverters.length > 0) {
                const invertersWS = XLSX.utils.json_to_sheet(reportData.inverters);
                XLSX.utils.book_append_sheet(wb, invertersWS, 'Inverters');
            }

            // Panels worksheet
            if (reportData.panels && reportData.panels.length > 0) {
                const panelsWS = XLSX.utils.json_to_sheet(reportData.panels);
                XLSX.utils.book_append_sheet(wb, panelsWS, 'Panels');
            }

            // Summary worksheet
            if (reportData.summary) {
                const summaryWS = XLSX.utils.json_to_sheet([reportData.summary]);
                XLSX.utils.book_append_sheet(wb, summaryWS, 'Summary');
            }

            // Asset movements worksheet
            if (reportData.assetMovements && reportData.assetMovements.length > 0) {
                const movementsWS = XLSX.utils.json_to_sheet(reportData.assetMovements);
                XLSX.utils.book_append_sheet(wb, movementsWS, 'Asset Movements');
            }

            const filename = `inventory_report_${reportData.summary.startDate}_to_${reportData.summary.endDate}.xlsx`;
            XLSX.writeFile(wb, filename);
            message.success('Report exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            message.error('Failed to export report');
        }
    };

    const handleCustomRangeReport = () => {
        if (!selectedDateRange) {
            message.warning('Please select a date range');
            return;
        }
        
        const startDate = formatDate(selectedDateRange[0].toDate());
        const endDate = formatDate(selectedDateRange[1].toDate());
        
        generateReport('custom', startDate, endDate);
        setDateRangeModalVisible(false);
    };

    const reportMenuItems = [
        {
            key: 'monthly',
            label: (
                <Button 
                    type="text" 
                    icon={<CalendarOutlined />}
                    onClick={() => setMonthPickerVisible(true)}
                    block
                    className="text-left"
                >
                    Monthly Report
                </Button>
            ),
        },
        {
            key: 'quarterly',
            label: (
                <Button 
                    type="text" 
                    icon={<CalendarOutlined />}
                    onClick={() => setQuarterPickerVisible(true)}
                    block
                    className="text-left"
                >
                    Quarterly Report
                </Button>
            ),
        },
        {
            key: 'custom',
            label: (
                <Button 
                    type="text" 
                    icon={<FileTextOutlined />}
                    onClick={() => setDateRangeModalVisible(true)}
                    block
                    className="text-left"
                >
                    Custom Range
                </Button>
            ),
        },
    ];

    const reportColumns = {
        inverters: [
            { title: 'Model', dataIndex: 'model', key: 'model' },
            { title: 'Item Name', dataIndex: 'itemName', key: 'itemName' },
            { title: 'Total Stock', dataIndex: 'totalStock', key: 'totalStock', align: 'right' },
            { title: 'Warranty (Years)', dataIndex: 'warrantyMonths', key: 'warrantyMonths', align: 'right' },
            { title: 'HSN Code', dataIndex: 'hsnCode', key: 'hsnCode' },
        ],
        panels: [
            { title: 'Model', dataIndex: 'model', key: 'model' },
            { title: 'Item Name', dataIndex: 'itemName', key: 'itemName' },
            { title: 'Wattage', dataIndex: 'wattage', key: 'wattage', align: 'right' },
            { title: 'Total Stock', dataIndex: 'totalStock', key: 'totalStock', align: 'right' },
            { title: 'Warranty (Years)', dataIndex: 'warrantyMonths', key: 'warrantyMonths', align: 'right' },
            { title: 'HSN Code', dataIndex: 'hsnCode', key: 'hsnCode' },
        ],
        generalItems: [
            { title: 'Model', dataIndex: 'model', key: 'model' },
            { title: 'Item Name', dataIndex: 'itemName', key: 'itemName' },
            { title: 'Type', dataIndex: 'itemType', key: 'itemType' },
            { title: 'Total Stock', dataIndex: 'totalStock', key: 'totalStock', align: 'right' },
            { title: 'Warranty (Years)', dataIndex: 'warrantyMonths', key: 'warrantyMonths', align: 'right' },
            { title: 'HSN Code', dataIndex: 'hsnCode', key: 'hsnCode' },
        ],
        assets: [
            { title: 'Serial Number', dataIndex: 'serialNumber', key: 'serialNumber' },
            { title: 'Model', dataIndex: 'model', key: 'model' },
            { title: 'Item Type', dataIndex: 'itemType', key: 'itemType' },
            { title: 'Assigned To (Customer ID)', dataIndex: 'assignedTo', key: 'assignedTo' },
            { title: 'Assigned Date', dataIndex: 'assignedDate', key: 'assignedDate' },
            { title: 'Warranty Expiry', dataIndex: 'warrantyExpiry', key: 'warrantyExpiry' },
            { title: 'Capacity', dataIndex: 'capacity', key: 'capacity', align: 'right' },
        ],
    };

    const tabItems = [
        {
            key: '1',
            label: (
                <span className="flex items-center gap-2">
                    <AppstoreOutlined />
                    Products & Items
                </span>
            ),
            children: <ProductList />,
        },
        {
            key: '2',
            label: (
                <span className="flex items-center gap-2">
                    <QrcodeOutlined />
                    Serialized Assets
                </span>
            ),
            children: <AssetTracker />,
        },
    ];

    return (
        <div style={{ padding: isMobile ? '12px' : '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 24, fontWeight: 700 }}>Inventory Management</h1>
                {/* Generate Report Button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {loading && <Spin size="small" />}
                    <Dropdown
                        menu={{ items: reportMenuItems }}
                        placement="bottomRight"
                        trigger={['click']}
                    >
                        <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            loading={loading}
                            size={isMobile ? 'middle' : 'large'}
                        >
                            {isMobile ? 'Report' : 'Generate Report'}
                        </Button>
                    </Dropdown>
                </div>
            </div>

            <LowStockAlert />

            {/* Month Picker Modal */}
            <Modal
                title="Select Month for Report"
                open={monthPickerVisible}
                onOk={generateMonthlyReport}
                onCancel={() => {
                    setMonthPickerVisible(false);
                    setSelectedMonth(null);
                }}
                okText="Generate Report"
                cancelText="Cancel"
                width={isMobile ? '95vw' : 420}
                style={isMobile ? { top: 40 } : {}}
            >
                <div className="py-4 space-y-4">
                    <div>
                        <label className="block mb-2 font-medium">Year:</label>
                        <Select 
                            className="w-full"
                            value={selectedYear}
                            onChange={(value) => setSelectedYear(value)}
                        >
                            {availableYears.map(year => (
                                <Option key={year} value={year}>{year}</Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <label className="block mb-2 font-medium">Month:</label>
                        <Select 
                            className="w-full"
                            placeholder="Select month"
                            value={selectedMonth}
                            onChange={(value) => setSelectedMonth(value)}
                        >
                            {months.map(month => (
                                <Option key={month.value} value={month.value}>{month.label}</Option>
                            ))}
                        </Select>
                    </div>
                </div>
            </Modal>

            {/* Quarter Picker Modal */}
            <Modal
                title="Select Quarter for Report"
                open={quarterPickerVisible}
                onOk={generateQuarterlyReport}
                onCancel={() => {
                    setQuarterPickerVisible(false);
                    setSelectedQuarter(null);
                }}
                okText="Generate Report"
                cancelText="Cancel"
                width={isMobile ? '95vw' : 420}
                style={isMobile ? { top: 40 } : {}}
            >
                <div className="py-4 space-y-4">
                    <div>
                        <label className="block mb-2 font-medium">Year:</label>
                        <Select 
                            className="w-full"
                            value={selectedYear}
                            onChange={(value) => setSelectedYear(value)}
                        >
                            {availableYears.map(year => (
                                <Option key={year} value={year}>{year}</Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <label className="block mb-2 font-medium">Quarter:</label>
                        <Select 
                            className="w-full"
                            placeholder="Select quarter"
                            value={selectedQuarter}
                            onChange={(value) => setSelectedQuarter(value)}
                        >
                            {quarters.map(quarter => (
                                <Option key={quarter.value} value={quarter.value}>{quarter.label}</Option>
                            ))}
                        </Select>
                    </div>
                </div>
            </Modal>

            {/* Date Range Modal */}
            <Modal
                title="Select Date Range for Report"
                open={dateRangeModalVisible}
                onOk={handleCustomRangeReport}
                onCancel={() => {
                    setDateRangeModalVisible(false);
                    setSelectedDateRange(null);
                }}
                okText="Generate Report"
                cancelText="Cancel"
                width={isMobile ? '95vw' : 420}
                style={isMobile ? { top: 40 } : {}}
            >
                <div className="py-4">
                    <label className="block mb-2 font-medium">Date Range:</label>
                    <RangePicker 
                        className="w-full"
                        onChange={(dates) => setSelectedDateRange(dates)}
                        format="YYYY-MM-DD"
                        allowClear
                    />
                </div>
            </Modal>

            {/* Report Modal */}
            <Modal
                title={`Inventory Report (${reportData?.summary?.startDate} to ${reportData?.summary?.endDate})`}
                open={reportModalVisible && reportData !== null}
                onCancel={() => {
                    setReportModalVisible(false);
                    setReportData(null);
                }}
                width={isMobile ? '100%' : 1100}
                style={isMobile ? { top: 0, margin: 0, padding: 0, maxWidth: '100vw' } : {}}
                styles={isMobile ? { body: { padding: '12px' } } : {}}
                footer={[
                    <Button key="close" onClick={() => {
                        setReportModalVisible(false);
                        setReportData(null);
                    }}>
                        Close
                    </Button>,
                    <Button 
                        key="export" 
                        type="primary" 
                        icon={<DownloadOutlined />}
                        onClick={exportToExcel}
                    >
                        Export to Excel
                    </Button>,
                ]}
            >
                {loading ? (
                    <div className="text-center py-8">
                        <Spin size="large" />
                    </div>
                ) : reportData && (
                    <div className="space-y-6">
                        {/* Summary Cards */}
                        <Row gutter={[12, 12]}>
                            <Col xs={24} sm={8}>
                                <Card size="small" title="Inverters Summary">
                                    <p>Total Models: {reportData.summary.totalInvertersModels}</p>
                                    <p>Total Units: {reportData.summary.totalInverters}</p>
                                </Card>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Card size="small" title="Panels Summary">
                                    <p>Total Models: {reportData.summary.totalPanelsModels}</p>
                                    <p>Total Units: {reportData.summary.totalPanels}</p>
                                </Card>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Card size="small" title="Assets Summary">
                                    <p>Total: {reportData.summary.totalAssets}</p>
                                    <p>Assigned: {reportData.summary.assignedAssets}</p>
                                    <p>Available: {reportData.summary.availableAssets}</p>
                                    <p>Faulty: {reportData.summary.faultyAssets}</p>
                                    <p>Retired: {reportData.summary.retiredAssets}</p>
                                </Card>
                            </Col>
                        </Row>

                        {/* Inverters Table */}
                        {reportData.inverters && reportData.inverters.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Inverters</h3>
                                <Table 
                                    columns={reportColumns.inverters}
                                    dataSource={reportData.inverters}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: true }}
                                />
                            </div>
                        )}

                        {/* Panels Table */}
                        {reportData.panels && reportData.panels.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Solar Panels</h3>
                                <Table 
                                    columns={reportColumns.panels}
                                    dataSource={reportData.panels}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: true }}
                                />
                            </div>
                        )}

                        {/* General Items Table */}
                        {reportData.generalItems && reportData.generalItems.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-2">General Items</h3>
                                <Table
                                    columns={reportColumns.generalItems}
                                    dataSource={reportData.generalItems}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: true }}
                                />
                            </div>
                        )}

                        {/* Asset Movements */}
                        {reportData.assetMovements && reportData.assetMovements.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-2">Asset Movements (Assigned)</h3>
                                <Table 
                                    columns={reportColumns.assets}
                                    dataSource={reportData.assetMovements}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    scroll={{ x: true }}
                                />
                            </div>
                        )}

                        <div className="text-right text-gray-500 text-sm">
                            Generated: {reportData.generatedAt}
                        </div>
                    </div>
                )}
            </Modal>

            <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: isMobile ? 4 : 8 }}>
                <Tabs
                    activeKey={activeTab}
                    items={tabItems}
                    onChange={setActiveTab}
                    type="card"
                    size={isMobile ? 'small' : 'middle'}
                    className="inventory-tabs"
                />
            </div>
        </div>
    );
};

export default InventoryPage;
